<template>
  <div class="provider-response-forwarding">
    <v-row>
      <v-col cols="12" sm="6">
        <v-select
          :model-value="locked ? registeredPreference : enabled ? 'email' : 'fax'"
          :items="methodOptions"
          label="Communication method"
          :disabled="disabled || locked"
          :readonly="readonly"
          hide-details
          class="lh--force-white-background"
          @update:model-value="onMethodChange">
          <template #append-inner>
            <v-tooltip location="top" max-width="320" open-delay="100">
              <template #activator="{ props: tooltipProps }">
                <v-icon v-bind="tooltipProps" size="15" class="provider-response-forwarding__help">
                  mdi-help-circle-outline
                </v-icon>
              </template>
              <span>Default communication method with prescribing provider.</span>
            </v-tooltip>
          </template>
        </v-select>
      </v-col>
      <v-col v-if="enabled" cols="12" sm="6">
        <v-text-field
          :model-value="email"
          label="Provider email"
          placeholder="name@clinic.com"
          type="email"
          class="lh--force-white-background"
          :readonly="readonly"
          :disabled="disabled"
          :error-messages="emailErrorMessages"
          @update:model-value="onEmailChange" />
      </v-col>
    </v-row>
    <div v-if="locked" class="d-flex align-center flex-wrap ga-2 mt-2">
      <v-chip size="small" variant="tonal" color="info" label prepend-icon="mdi-account-check-outline">
        Set by the provider on their account
      </v-chip>
      <span class="text-caption text-medium-emphasis">
        This provider has a Do Prior Auth login and asked to be reached by {{ registeredPreference }}. That choice is
        used when sending to them.
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { isEmailValid } from "@/utils/validators";

const props = defineProps({
  enabled: { type: Boolean, default: false },
  email: { type: String, default: null },
  disabled: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
  showErrors: { type: Boolean, default: false },
  // The registered provider login's own choice (LAM-5508). When set, the method follows it and the select
  // is disabled: LAM-5263 already sends by this channel regardless of what a tenant picks here. The email
  // typed beside it stays the tenant's fallback — a send to a registered login goes to its login email,
  // which is never shown here.
  registeredPreference: { type: String, default: null },
});

const emit = defineEmits(["update:enabled", "update:email"]);

const methodOptions = [
  { title: "Fax", value: "fax" },
  { title: "Email", value: "email" },
];

const locked = computed(() => props.registeredPreference === "email" || props.registeredPreference === "fax");

const emailErrorMessages = computed(() => {
  if (!props.showErrors || !props.enabled) return null;
  if (!props.email || !String(props.email).trim()) {
    return "Provider email is required when forwarding responses.";
  }
  if (!isEmailValid(props.email)) {
    return "Please enter a valid email address.";
  }
  return null;
});

function onMethodChange(value) {
  if (locked.value) return;
  emit("update:enabled", value === "email");
}

function onEmailChange(value) {
  emit("update:email", value);
}

function validate() {
  if (!props.enabled) return true;
  return Boolean(props.email && String(props.email).trim() && isEmailValid(props.email));
}

defineExpose({ validate });
</script>

<style lang="scss" scoped>
@import "@/styles/_variables";

.provider-response-forwarding__help {
  margin-left: 4px;
  color: $lh_color_blue_gray;
  cursor: help;
}
</style>
